// ui/game-over.js: pure helpers for the M2.c-10e GameOverOverlay.
//
// Helpers live in a .js file (not the .jsx) so unit tests can import
// them under Node's native ESM loader without an .jsx transform step.
// Same pattern as ui/end-of-hand.js relative to EndOfHandModal.jsx.

export const TEAM_LABEL = Object.freeze({ team_a: 'US', team_b: 'THEM' });

// B4a-F6: the game-over card tells everyone that teams and settings
// carry over and who starts the next game. Ready stays optional: the
// host's Start is enabled at once back in the lobby.
export const GAME_OVER_NEXT_GAME_TEXT =
  'Same teams and settings. The host starts the next game.';

/**
 * Pure helper: which team won the game?
 *   - Prefer explicit winningTeam (state.winning_team from state.lua)
 *   - Fall back to summary.delta_team when the summary marks game_over
 *   - Fall back to whichever team has the higher score
 *   - Returns null when no signal is available
 *
 * @param {{
 *   winningTeam?: 'team_a'|'team_b'|null,
 *   summary?: { delta_team?: string, game_over?: boolean }|null,
 *   scores?: { team_a: number, team_b: number }|null,
 * }} args
 * @returns {'team_a'|'team_b'|null}
 */
export function deriveWinner({ winningTeam, summary, scores }) {
  if (winningTeam === 'team_a' || winningTeam === 'team_b') return winningTeam;
  if (summary && summary.game_over) {
    const dt = summary.delta_team;
    if (dt === 'team_a' || dt === 'team_b') return dt;
  }
  if (scores) {
    if (scores.team_a > scores.team_b) return 'team_a';
    if (scores.team_b > scores.team_a) return 'team_b';
  }
  return null;
}

/**
 * The game-over title, read from the viewer's side in the words the
 * line under it uses ("They won 7–4"): "WE WON THE GAME" or "THEY WON
 * THE GAME". A viewer with no team reads the winning team's label; with
 * no winner it is "GAME OVER".
 *
 * @param {'team_a'|'team_b'|null} winner
 * @param {'team_a'|'team_b'|null|undefined} viewerTeam
 * @returns {string}
 */
export function gameOverHeadline(winner, viewerTeam) {
  if (!winner) return 'GAME OVER';
  if (!viewerTeam) return `${TEAM_LABEL[winner]} WON THE GAME`;
  return viewerTeam === winner ? 'WE WON THE GAME' : 'THEY WON THE GAME';
}

/**
 * Pure helper: human-readable score line "9–8". Returns '' when no
 * scores are available.
 *
 * @param {{team_a:number,team_b:number}|null|undefined} scores
 */
export function formatScoreLine(scores) {
  if (!scores) return '';
  return `${scores.team_a}–${scores.team_b}`;
}

/**
 * B2-8: the networked game-over headline from the viewer's side.
 * "You won 7–5" when the viewer's team took it, "They won 7–5"
 * otherwise. The WINNER's total leads, so the losing side never reads
 * first.
 *
 * @param {{team_a:number,team_b:number}|null|undefined} scores
 * @param {'team_a'|'team_b'|null|undefined} viewerTeam
 * @param {'team_a'|'team_b'|null} winnerTeam
 * @returns {string}  e.g. "You won 7–5", or '' without enough signal
 */
export function formatViewerResult(scores, viewerTeam, winnerTeam) {
  if (!scores || !viewerTeam || !winnerTeam) return '';
  const winnerScore = scores[winnerTeam] ?? 0;
  const loserTeam = winnerTeam === 'team_a' ? 'team_b' : 'team_a';
  const loserScore = scores[loserTeam] ?? 0;
  const verb = winnerTeam === viewerTeam ? 'You won' : 'They won';
  return `${verb} ${winnerScore}–${loserScore}`;
}

/**
 * B2-8: the game-over acknowledgement countdown, "Back to lobby in N s".
 *
 * @param {number|null|undefined} deadlineMs  ready_deadline_server_ts
 * @param {number} nowMs  local clock (Date.now())
 * @param {number} [clockOffsetMs]  smoothed server-minus-client offset
 * @returns {string|null}
 */
export function lobbyCountdownText(deadlineMs, nowMs, clockOffsetMs = 0) {
  if (deadlineMs == null) return null;
  const remaining = Math.max(0, deadlineMs - (nowMs + clockOffsetMs));
  return `Back to lobby in ${Math.ceil(remaining / 1000)} s`;
}
